// src/services/subscriptionApi.ts
// ============================================================================
// MI PLAN (fase C, 18-ago-2026) — estado de la suscripción del estudio y cómo pagarla.
// Port del subscriptionService de la agenda dental, sobre el apiClient de este front.
// Todo sale de lealify-base (/api/billing/*): el MONTO SIEMPRE lo manda el servidor
// (precio acordado si lo hay) y la lista de beneficios viaja en plan_features
// (fuente única lib/planFeatures.js — aquí solo se pinta).
// ============================================================================
import { api } from './apiClient';

export interface PlanSubscription {
  plan_code: string;                 // 'free' | 'pro_yearly' | 'pro_monthly'
  status: string;
  started_at: string | null;
  expires_at: string | null;         // null = sin vencimiento (cortesía permanente)
  is_trial: 0 | 1;                   // 1 = cortesía (no se le cobra todavía)
  is_pro: boolean;
  days_left: number | null;
  expired: boolean;
  price: number;                     // lo que paga POR PERIODO (acordado si lo tiene)
  currency: string;
  plan_months: number;               // 12 = anual · 1 = mensual · 0 = gratis
  price_is_custom?: boolean;
  plan_features?: string[];          // qué incluye el plan (por vertical, lo manda el servidor)
  plan_features_rich?: PlanFeatureRich[] | null;  // versión con ícono+título+desc; null → checklist
  // Domiciliación (30-ago-2026): null = nunca; 'authorized' = pago automático ACTIVO.
  autopay_status?: 'pending' | 'authorized' | 'paused' | 'cancelled' | null;
  // Periodos elegibles con su monto y si se pueden elegir AHORA (27-sep-2026): PLAN_LOCKED =
  // precio acordado (solo su plan) · AT_RENEWAL = acortar periodo solo a ≤30 días de renovar.
  plan_options?: PlanOption[];
}

export interface PlanOption {
  code: string; months: number; label: string; amount: number; currency: string;
  current: boolean; allowed: boolean; reason: 'PLAN_LOCKED' | 'AT_RENEWAL' | null;
}

export interface PlanFeatureRich {
  icon: string;                      // nombre genérico ('calendar', 'wallet'…); el front lo mapea
  title: string;
  desc: string;
}

export interface PlanPayment {
  id: number;
  plan_code: string;
  amount: number | string;
  currency: string;
  method: 'manual' | 'mercadopago' | 'btcpay';
  status: 'pending' | 'completed' | 'failed' | 'cancelled';
  checkout_url: string | null;
  period_end: string | null;
  created_at: string;
  approved_at: string | null;
}

export interface BankAccount {
  bank?: string;
  accountName?: string;
  accountNumber?: string;
  clabe?: string;
}

export interface PaymentMethods {
  available: string[];
  bank_account: BankAccount | null;
}

/** Plan vigente + historial de pagos del estudio. */
export function getMyPlan(): Promise<{ subscription: PlanSubscription; payments: PlanPayment[] }> {
  return api.get('/billing/subscription');
}

/** Formas de pago activas + datos bancarios para transferencia. */
export async function getPaymentMethods(): Promise<PaymentMethods> {
  const r = await api.get<{ available?: string[]; bank_account?: BankAccount | null }>('/billing/payment-methods');
  return { available: r.available || [], bank_account: r.bank_account || null };
}

/**
 * Inicia un cobro por Mercado Pago y devuelve la liga del checkout. El plan NO se activa
 * aquí: lo cierra el webhook cuando MP confirma. `return_path` + el Origin arman los
 * back_urls para volver a ESTA app tras pagar (no al manager).
 */
export async function startMercadoPago(planCode?: string | null): Promise<{ paymentId: number; checkoutUrl: string }> {
  // `plan_code` (27-sep-2026) = periodo elegido; sin él el servidor cobra el plan vigente.
  const r = await api.post<{ payment_id: number; checkout_url: string }>(
    '/billing/payments/mercadopago', { return_path: '/mi-plan', plan_code: planCode ?? null });
  return { paymentId: r.payment_id, checkoutUrl: r.checkout_url };
}

/**
 * DOMICILIACIÓN (30-ago-2026): autoriza la tarjeta UNA vez y el cobro sale solo cada ciclo
 * (suscripción MP). El primer cargo respeta la vigencia ya pagada. Cancelable cuando quiera.
 */
export async function startAutopay(planCode?: string | null): Promise<{ checkoutUrl: string }> {
  const r = await api.post<{ checkout_url: string }>('/billing/autopay/start', { return_path: '/mi-plan', plan_code: planCode ?? null });
  return { checkoutUrl: r.checkout_url };
}

/** Apaga la domiciliación. El plan vigente NO se toca: solo deja de auto-cobrarse. */
export async function cancelAutopay(): Promise<void> {
  await api.post('/billing/autopay/cancel', {});
}

/** Registra una transferencia con su comprobante. Queda PENDIENTE hasta que se apruebe. */
export async function registerTransfer(receiptUrl: string, planCode?: string | null): Promise<{ id: number }> {
  const r = await api.post<{ payment: { id: number } }>('/billing/payments/manual', { receipt_url: receiptUrl, plan_code: planCode ?? null });
  return { id: r.payment.id };
}

/**
 * Sube el COMPROBANTE (imagen JPG/PNG/WEBP ≤5 MB; el uploader no acepta PDF) y devuelve su URL pública. Token HMAC de un solo
 * uso (~2 min) emitido por lealify-base; la subida real va al uploader de api.lealify.com.
 */
export async function uploadReceipt(file: File): Promise<string> {
  const { token } = await api.get<{ token: string }>('/uploads/token');
  const fd = new FormData();
  fd.append('image', file); // el uploader hace upload.single('image'): con 'file' devolvía 500
  const r = await fetch('https://api.lealify.com/upload', {
    method: 'POST', headers: { 'x-upload-token': token }, body: fd,
  });
  if (!r.ok) throw new Error('No se pudo subir el comprobante (usa una foto o captura JPG/PNG de hasta 5 MB)');
  const data = (await r.json()) as { url?: string };
  if (!data.url) throw new Error('El servidor no devolvió la URL del comprobante');
  return data.url.startsWith('http') ? data.url : 'https://api.lealify.com' + data.url;
}

/** Texto del periodo para la interfaz: "al mes" / "al año". */
export const periodoTexto = (meses: number): string => (meses === 1 ? 'al mes' : 'al año');

/** Formato de dinero consistente con el resto de la app. */
export const money = (n: number | string, currency = 'MXN'): string =>
  `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${currency}`;
